import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { displayToastSuccess } from '@/helpers';

import { unapproveWorklogs } from '../api/time-tracking.api';
import { APPROVALS_QUERY_KEYS } from '../constants/approvals-query-keys';
import { getApprovalSelectionCountForMode } from '../helpers/approval-selection-counts.helper';
import { useApprovalsStore } from '../store/approvals.store';
import { GetApprovalFilterRequest } from '../types/dto/worklogs-approval.dto';
import {
  ApprovalConfirmMode,
  ApprovalSelection,
  ApprovalSelectionLevel,
  TimeEntryStatus,
} from '../types/time-tracking-approvals';
import { patchApprovalWorklogs } from '../utils/patch-approval-worklogs';
import { invalidateApprovalAggregates } from '../utils/time-tracking-query-invalidation';

interface UnapproveWorklogsParams {
  selection: ApprovalSelection;
  filter: GetApprovalFilterRequest;
}

export const useUnapproveWorklogs = () => {
  const queryClient = useQueryClient();
  const { t } = useTranslation('authenticated.timeTracking');

  return useMutation({
    mutationFn: ({ selection, filter }: UnapproveWorklogsParams) =>
      unapproveWorklogs({ selection, filter }),
    onSuccess: async (_, { selection }) => {
      if (selection.level === ApprovalSelectionLevel.Worklogs) {
        const { entries } = selection;
        // Patch only the changed worklog rows in place (status → Submitted), instead of
        // refetching every loaded worklog page (heavy + laggy when many are open).
        patchApprovalWorklogs({
          queryClient,
          entries,
          status: TimeEntryStatus.SUBMITTED,
        });

        // Edits are now committed server-side — clear them so the page is no longer dirty.
        useApprovalsStore.getState().clearEditedEntries(entries.map(entry => entry.id));
      } else {
        // Project/member level unapproval changes worklogs that may not even be loaded —
        // targeted patching is impossible, so refetch the loaded worklog sections for
        // fresh statuses and hashes, and drop the aggregate-row selection.
        await queryClient.invalidateQueries({ queryKey: APPROVALS_QUERY_KEYS.worklogsAll });
        useApprovalsStore.getState().clearRowSelection();
      }

      // Refresh only the lightweight aggregates (summary tiles + project totals).
      await invalidateApprovalAggregates(queryClient);

      const count = getApprovalSelectionCountForMode(selection, ApprovalConfirmMode.Unapprove);
      const message =
        count === 1
          ? t('approvals.toasts.unapprovedSingle')
          : t('approvals.toasts.unapprovedBulk', { count });
      displayToastSuccess(message);
    },
  });
};
